/**
 * How the garden grows outward: this game's districts.
 *
 * Content, not engine. The arithmetic over these rows (`tierFor`,
 * `capacityFor`, `tierForSlot`, `nextTier`) is `src/game/tiers.ts`. A tier's
 * *index* is what saves and share links store, so never reorder or remove a
 * row once it has shipped — only append.
 *
 * The plot starts as one terrace of twelve beds and opens into four
 * concentric districts, each unlocked by trees grown. This is the long
 * progression: not a bigger number on a HUD, but visibly more *place*.
 *
 * ## Each district teaches the one after it
 *
 * Every ring after the first is a kind of garden — a fourfold water garden,
 * a sunken shaded walk, a reed-bed wetland — and unlocking it hands over the
 * fact card that explains how that kind of garden works. **The expansion is
 * the teaching device**, the same way a mutation is: a player who has opened
 * the whole plot has been told, in instalments they earned, four different
 * answers to what a garden can be.
 *
 * No claim is written here — every district points at a card in `facts.ts`.
 * See `docs/CONTENT.md`.
 *
 * ## Why slot radii are absolute metres
 *
 * A tier changes the plot's outer radius, so it is tempting to place beds at
 * a fraction of it. Doing that moves every existing bed the moment the garden
 * grows, and a planted specimen would either jump to a new position or come
 * adrift from its slot. Bed rings are therefore fixed in metres and never
 * move once opened; only the ground beneath them extends.
 */

export type TierId = 'terrace' | 'charbagh' | 'wadi' | 'wetland';

export interface TierRing {
  /** Beds on this ring. */
  count: number;
  /** Distance from the plot centre, metres. Absolute and permanent. */
  radius: number;
  /** Rotation offset so a new ring interleaves with the one inside it. */
  phase: number;
}

export interface Tier {
  id: TierId;
  name: string;
  nameAr: string;
  /** Lifetime specimens brought to maturity before this opens. */
  treesNeeded: number;
  /** Outer radius of the terrace once this tier is open, metres. */
  radius: number;
  /** Bed rings this tier adds. */
  rings: TierRing[];
  /** One line, shown on the unlock toast. */
  blurb: string;
  /** The sourced card handed over on unlock. Null for the starting terrace. */
  factId: string | null;
}

/**
 * Four tiers, 54 beds.
 *
 * Ring counts are set by arc spacing, not by a round number: every ring keeps
 * its beds about 0.47 m apart, which is wider than the largest pick radius
 * and close enough that a district reads as planted rather than as a plaza
 * with a few shrubs on it. The first pass used tidier counts and the outer
 * rings came out half as dense as the terrace — a bigger garden that looked
 * emptier, which is the opposite of the point.
 *
 * The gates are deliberately front-loaded in the same way the grow ladder is:
 * the first expansion lands inside a single good session, so a visitor who
 * only ever opens this once still sees the garden *become* something. The
 * fourth is a fortnight of returning, and is meant to be.
 *
 * **Every gate must be reachable by planting alone.** Each threshold sits
 * below the capacity of the tier before it — 8 under 12, 20 under 22, 34
 * under 36. The wetland was originally 40, which is more beds than tier 2
 * has: a player who never harvested could not have reached it, which would
 * have quietly made an explicitly optional mechanic compulsory for the last
 * district. Check this again if a ring count ever changes.
 */
export const TIERS: Tier[] = [
  {
    id: 'terrace',
    name: 'Palm Terrace',
    nameAr: 'مصطبة النخيل',
    treesNeeded: 0,
    radius: 0.7,
    rings: [
      { count: 4, radius: 0.21, phase: 0 },
      { count: 8, radius: 0.46, phase: Math.PI / 8 },
    ],
    blurb: 'A single terrace of twelve beds, and nothing in them yet.',
    factId: null,
  },
  {
    id: 'charbagh',
    name: 'Charbagh Oasis',
    nameAr: 'الحديقة الرباعية',
    treesNeeded: 8,
    radius: 0.99,
    rings: [{ count: 10, radius: 0.75, phase: Math.PI / 10 }],
    blurb: 'Fourfold geometry, and water running to the four quarters.',
    factId: 'charbagh',
  },
  {
    id: 'wadi',
    name: 'Sunken Eco-Wadi',
    nameAr: 'الوادي الغائر',
    treesNeeded: 20,
    radius: 1.29,
    rings: [{ count: 14, radius: 1.05, phase: Math.PI / 14 }],
    blurb: 'A walk cut below grade, where the air runs cooler.',
    factId: 'wadi',
  },
  {
    id: 'wetland',
    name: 'Aquatic Wetland',
    nameAr: 'الأراضي الرطبة',
    treesNeeded: 34,
    radius: 1.6,
    rings: [{ count: 18, radius: 1.37, phase: Math.PI / 18 }],
    blurb: 'Reed beds, still water and a boardwalk to the four gates.',
    factId: 'wetland',
  },
];
